// apps/web/src/lib/kanban/operador-tablet.ts — Fase 17.9, Plano 02 (KAN-06, KAN-07, KAN-08, D-03..D-14).
import type { KanbanStreamTicket, TabletOperadorResumo } from '@pieltec/contracts';
import { TECLA_APAGAR, TECLA_LIMPAR } from '../estoque/teclado-peso';
import { cabecalhosDispositivo, type CredencialDispositivo } from './dispositivo';

export function aplicarTeclaPin(pin: string, tecla: string): string {
  if (tecla === TECLA_LIMPAR || tecla === 'limpar' || tecla === 'C') {
    return '';
  }
  if (tecla === TECLA_APAGAR || tecla === 'apagar' || tecla === '⌫' || tecla === 'Backspace') {
    return pin.slice(0, -1);
  }
  if (!/^\d$/.test(tecla)) {
    return pin;
  }
  if (pin.length >= 4) {
    return pin;
  }
  return pin + tecla;
}

export function formatarPontosPin(pin: string): string {
  const pontos: string[] = [];
  for (let i = 0; i < 4; i++) {
    pontos.push(i < pin.length ? '●' : '○');
  }
  return pontos.join(' ');
}

export type MotivoOperadorDesabilitado = 'sem_pin' | 'aguardando_envio';

export interface OperadorClassificado {
  readonly operador: TabletOperadorResumo;
  readonly desabilitado: boolean;
  readonly motivo: MotivoOperadorDesabilitado | null;
}

/**
 * Autores com apontamentos pendentes que NÃO conseguem mais entrar no tablet (desativados, sem PIN
 * ou fora da lista de elegíveis por troca de perfil). Seus eventos não podem ser enviados por outra
 * conta (parada por autor), então só um supervisor resolve; a seleção dos demais operadores não pode
 * ficar presa a eles (WR-03).
 */
export function autoresPendentesInelegiveis(
  operadores: readonly TabletOperadorResumo[],
  autoresPendentes: readonly string[],
): string[] {
  const elegiveis = new Set(operadores.filter((op) => op.temPin).map((op) => op.id));
  return autoresPendentes.filter((id) => !elegiveis.has(id));
}

/**
 * Regra de seleção do modal (D-14): sem PIN não entra; havendo apontamentos pendentes de algum autor
 * que ainda pode entrar, só esses autores podem entrar (para enviar a fila antes de trocar de operador).
 * Autores pendentes inelegíveis não bloqueiam ninguém (WR-03). O modal usa esta função e a regra fica
 * testável sem renderizar.
 */
export function classificarOperadores(
  operadores: readonly TabletOperadorResumo[],
  autoresPendentes: readonly string[],
): OperadorClassificado[] {
  const inelegiveis = new Set(autoresPendentesInelegiveis(operadores, autoresPendentes));
  const autoresBloqueantes = autoresPendentes.filter((id) => !inelegiveis.has(id));
  return operadores.map((operador) => {
    if (!operador.temPin) return { operador, desabilitado: true, motivo: 'sem_pin' };
    if (autoresBloqueantes.length > 0 && !autoresBloqueantes.includes(operador.id)) {
      return { operador, desabilitado: true, motivo: 'aguardando_envio' };
    }
    return { operador, desabilitado: false, motivo: null };
  });
}

export function montarUrlStreamTablet(apiBase: string, deviceId: string, ticket: string): string {
  const base = apiBase.replace(/\/$/, '');
  return `${base}/api/v1/kanban/tablet/stream?deviceId=${encodeURIComponent(deviceId)}&ticket=${encodeURIComponent(ticket)}`;
}

export async function buscarUrlStreamTablet(
  cred: CredencialDispositivo,
  apiBase = '',
): Promise<string | null> {
  const base = apiBase.replace(/\/$/, '');
  const resposta = await fetch(`${base}/api/v1/kanban/tablet/stream-ticket`, {
    headers: cabecalhosDispositivo(cred),
  });
  if (resposta.status === 401 || resposta.status === 403) {
    return null;
  }
  if (!resposta.ok) {
    throw new Error(`Falha ao obter ticket do stream: HTTP ${resposta.status}`);
  }
  const dados = (await resposta.json()) as KanbanStreamTicket;
  return montarUrlStreamTablet(apiBase, cred.deviceId, dados.ticket);
}
